import { CheckOutlined } from '@ant-design/icons';
import { Button, Input, Spinner } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { OnClickElsewhere } from '../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { colors } from '../../styles/colors';
import styles from './AudienceNameEditBar.module.css';
import { getESStructure } from './Audiences';
import { useAudienceNameValidation } from './use-audience-name-validation';

interface Props {
  activeAudience: AudiencesModel;
  onEndEditing: () => void;
  className?: string;
  css?: {
    input?: string;
  };
  debounceTimeout?: number;
}
export const AudienceNameEditBar = (props: Props) => {
  const { activeAudience, onEndEditing, className, css, debounceTimeout } =
    props;
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const [newAudienceName, setNewAudienceName] = useState(activeAudience?.name);
  const trimmedNewAudienceName = newAudienceName.trim();
  const debouncedAudienceName = useDebounce(
    trimmedNewAudienceName,
    debounceTimeout || 250,
  );
  const { validateAudienceName } = useAudienceNameValidation();
  const [newAudienceNameStatus, setNewAudienceNameStatus] = useState<{
    dirty: boolean;
    valid?: boolean;
    saving?: boolean;
  }>({ dirty: false });
  const newAudienceNameChanged =
    trimmedNewAudienceName !== activeAudience?.name;
  useEffect(
    () =>
      setNewAudienceNameStatus({
        dirty: newAudienceNameChanged,
        valid:
          newAudienceNameChanged && trimmedNewAudienceName
            ? undefined
            : !!trimmedNewAudienceName,
      }),
    [trimmedNewAudienceName, newAudienceNameChanged],
  );
  const onRenameAudience = async () => {
    if (!newAudienceNameStatus.dirty) {
      onEndEditing();
      return;
    }
    setNewAudienceNameStatus(status => ({ ...status, saving: true }));
    try {
      await audiencesCRUDActions.update(
        {
          ...activeAudience,
          esFilters: getESStructure(activeAudience.esFilters),
          name: debouncedAudienceName,
        },
        activeChatbotId,
      );
      setNewAudienceNameStatus({ dirty: false });
    } finally {
      setNewAudienceNameStatus(status => ({ ...status, saving: false }));
      onEndEditing();
    }
  };
  useEffect(() => {
    if (debouncedAudienceName) {
      setNewAudienceNameStatus(status => ({ ...status, checking: true }));
      (async () => {
        const valid = await validateAudienceName(debouncedAudienceName);
        setNewAudienceNameStatus(status => ({
          ...status,
          valid,
          checking: false,
        }));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAudienceName]);

  return (
    <OnClickElsewhere handleClick={onEndEditing}>
      <div className={classNames(className, styles.container)}>
        <Input
          css={{ container: classNames(styles.input, css?.input) }}
          type="text"
          invalid={
            newAudienceNameStatus.dirty && newAudienceNameStatus.valid === false
          }
          onChange={e => setNewAudienceName(e.target.value)}
          value={newAudienceName}
        />

        <Button
          disabled={
            !newAudienceNameStatus.valid || newAudienceNameStatus.saving
          }
          onClick={onRenameAudience}
          css={{ container: styles.save }}
          leftIcon={<CheckOutlined className={styles.checkmark} />}
        />
        {(newAudienceNameStatus.saving ||
          newAudienceNameStatus.valid === undefined) && (
          <div>
            <Spinner
              show
              size={14}
              position="relative"
              color={colors.accentBlue}
              backgroundColor="transparent"
            />
          </div>
        )}
        {trimmedNewAudienceName &&
          newAudienceNameStatus.dirty &&
          newAudienceNameStatus.valid === false && (
            <div className={styles.error}>
              An audience with that name already exists
            </div>
          )}
      </div>
    </OnClickElsewhere>
  );
};
